// PC-4b.3.3a: client mirror of the server's template helpers
// (apps/server/src/lib/bridges/bridge-templates.ts). Presentation only — these
// let the picker size param inputs, decide enable/disable, and render a live
// preview without importing server code. The server is authoritative.

// Mirrors the server's BridgeTemplateRow wire shape (the GET returns rows as-is).
export interface Template {
  id: string;
  bridge_account_id: string;
  name: string;
  language: string;
  category: string | null;
  status: string;
  components: unknown[];
  source: 'meta' | 'manual';
  synced_at: string | null;
}

function findBody(components: unknown[]): Record<string, unknown> | undefined {
  return (components as Array<Record<string, unknown>>).find(
    (c) => typeof c?.type === 'string' && (c.type as string).toUpperCase() === 'BODY');
}
function findButtons(components: unknown[]): Array<Record<string, unknown>> {
  const c = (components as Array<Record<string, unknown>>).find(
    (x) => typeof x?.type === 'string' && (x.type as string).toUpperCase() === 'BUTTONS');
  return c && Array.isArray(c.buttons) ? (c.buttons as Array<Record<string, unknown>>) : [];
}

export function templateBodyParamCount(components: unknown[]): number {
  const body = findBody(components);
  if (!body) return 0;
  const example = body.example as { body_text?: unknown[] } | undefined;
  const firstRow = example?.body_text?.[0];
  if (Array.isArray(firstRow)) return firstRow.length;
  const text = typeof body.text === 'string' ? body.text : '';
  const matches = text.match(/\{\{\s*(\d+)\s*\}\}/g);
  return matches ? new Set(matches.map((m) => m.replace(/[^\d]/g, ''))).size : 0;
}

export function templateUrlButtonParams(components: unknown[]): Array<{ index: string; label: string }> {
  const out: Array<{ index: string; label: string }> = [];
  findButtons(components).forEach((b, i) => {
    const isUrl = typeof b?.type === 'string' && (b.type as string).toUpperCase() === 'URL';
    const url = typeof b?.url === 'string' ? b.url : '';
    if (isUrl && /\{\{\s*\d+\s*\}\}/.test(url)) {
      out.push({ index: String(i), label: typeof b.text === 'string' ? b.text : `Button ${i + 1}` });
    }
  });
  return out;
}

export function templateHeaderFormat(components: unknown[]): 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'TEXT' | null {
  const header = (components as Array<Record<string, unknown>>).find(
    (c) => typeof c?.type === 'string' && (c.type as string).toUpperCase() === 'HEADER');
  if (!header) return null;
  const fmt = typeof header.format === 'string' ? header.format.toUpperCase() : 'TEXT';
  if (fmt === 'IMAGE' || fmt === 'VIDEO' || fmt === 'DOCUMENT' || fmt === 'TEXT') return fmt;
  return 'TEXT';
}

export function templateIsSendableInApp(t: Template): boolean {
  return t.status === 'APPROVED';
}

export function unsendableReason(t: Template): string {
  if (t.status === 'REJECTED') return 'Rejected by Meta';
  return 'Pending approval';
}

export function renderTemplatePreview(components: unknown[], bodyParams: string[]): string {
  const body = findBody(components);
  const text = body && typeof body.text === 'string' ? body.text : '';
  if (!text) return '';
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n: string) => bodyParams[Number(n) - 1] || `{{${n}}}`);
}
